<template>
    <Accordion style="margin-bottom: 0.5em;direction: rtl;" :activeIndex="filterOpened" class="w-full">
        <AccordionTab>
            <template #header>
                <div class="text-blue-500">
                    <i :class="['pi', 'pi-filter']" style="margin: 0 0.5em 0 0.5em; font-weight: bold;" />
                    <span class="text-blue-500 font-bold">{{ $t('SEARCH') }}</span>
                </div>
            </template>
            <div class="flex flex-wrap justify-content-center filter_container">
                <MultiSelect v-model="selectedOwners" :options="owners" optionLabel="fullName" filter
                    :placeholder="$t('OWNER')" display="chip">
                    <template #option="slotProps">
                        <div class="flex align-items-center">
                            <img class="filter_avatar" :src="slotProps.option.imageUrl" />
                            <div>{{ slotProps.option.fullName }}</div>
                        </div>
                    </template>
                </MultiSelect>
                <MultiSelect v-model="selectedStatuses" :options="statuses" optionLabel="name"
                    :placeholder="$t('STATUS')" />
                <MultiSelect v-model="selectedOperationTypes" :options="types" optionLabel="name"
                    :placeholder="$t('TYPE')" />
                <Calendar v-model="selectedCreationDate" :placeholder="$t('CREATION_DATE')" />
                <Calendar v-model="selectedExpirationDate" :placeholder="$t('EXPIRATION_DATE')" />
                <div class="flex gap-1">
                    <Button class="flex-1 align-items-center" @click="filterOperations">
                        <i :class="['pi', 'pi-search']" />
                        <span class="m-auto">{{ $t('SEARCH') }}</span>
                    </Button>
                    <Button class="flex-1" @click="resetFilter">
                        <i :class="['pi', 'pi-eraser']" />
                        <span class="m-auto">{{ $t('CLEAR') }}</span>
                    </Button>
                </div>
            </div>
        </AccordionTab>
    </Accordion>
    <MyPaginator ref="paginator" :rowsPerPage="pageSize" :totalRecords="totalOperationsCount" @pageSelected="changePage" dir="rtl" class="w-full"/>
    <div class="flex flex-wrap justify-content-center gap-2" style="margin-top: 0.5em;direction: rtl">
        <div v-for="operation in operations" :key="operation" class="flex-item">
            <Operation :operation="operation" @operationStatusChanged="handleOperationStatusChanged"
                @senderEvaluated="handleSenderEvaluated" @receiverEvaluated="handleReceiverEvaluated"
                @operationErrorOccurred="handleOperationError" @messagesReadMark="e => emit('messages-read-mark', e)"
                @incidenceRegistered="handleIncidenceRegistered" />
        </div>
        <div v-if="shouldShowEmptyResults()">
            <font-awesome-icon :icon="['fas', 'ellipsis']" size="2xl" />

            {{ $t('EMPTY') }}

        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onErrorCaptured, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useToast } from "primevue/usetoast"
import { useI18n } from 'vue-i18n'
import { handleError } from '@/helpers/ErrorHandler'
import Operation from '@/components/Operation.vue'
import MyPaginator from '@/components/MyPaginator.vue'
import OperationFilter from '@/model/OperationFilter'
import OperationsPageRequest from '@/model/OperationsPageRequest'
import { operationStatusEnum, operationTypeEnum } from '@/helpers/Constants'
import bookService from '@/service/BookService'
import { uniqBy, remove } from 'lodash'

const emit = defineEmits(['filter-triggered', 'page-requested', 'messages-read-mark'])

const props = defineProps({
    pageSize: {
        type: Number,
        required: true,
    },
    totalOperationsCount: {
        type: Number,
        required: true
    },
    operations: {
        type: Array,
        required: true,
        default: () => []
    }
})

const authStore = useAuthStore()
const toast = useToast()
const i18n = useI18n()

// Pagination data
const paginator = ref(null)
const operations = ref([])

// Filters
const selectedOwners = ref([])
const selectedStatuses = ref([])
const selectedOperationTypes = ref([])
const selectedCreationDate = ref(null)
const selectedExpirationDate = ref(null)
const filterOpened = ref(-1)

// Dropdown
const owners = ref([])
const statuses = ref([])
const types = ref([])

onMounted(async () => {
    try {
        owners.value = await bookService.getOwners()
        remove(owners.value, o => o.id === authStore.user.id)
        makeStatusDropdown()
        makeTypesDropdown()
    } catch (error) {
        handleError(authStore, toast, i18n, error)
    }
})

onErrorCaptured((err, instance, info) => {
    return handleError(authStore, toast, i18n, err, instance, info)
})

const handleOperationError = (e) => {
    handleError(authStore, toast, i18n, e)
}

// watchs
watch(() => props.operations, (newValue) => {
    operations.value = [...newValue]
})

// Events
const filterOperations = () => {
    emit('filter-triggered', makeOperationFilter())
    paginator.value.resetPagination()
}

const changePage = (requestPage) => {
    emit('page-requested', new OperationsPageRequest(requestPage, makeOperationFilter()))
}

const shouldShowEmptyResults = () => {
    return operations.value === null || operations.value === undefined || operations.value.length === 0
}

const handleOperationStatusChanged = (operationId, newStatus, feedbackMessage) => {
    const operation = operations.value.filter(o => o.id === operationId)[0]
    operation.statusId = newStatus
    if (newStatus === operationStatusEnum.COMPLETED) {
        operation.daysToExpire = null
    }
    toast.add({ severity: 'info', summary: feedbackMessage, detail: '', life: 3000 })
}

const handleSenderEvaluated = (operationId) => {
    operations.value.filter(o => o.id === operationId)[0].senderEvaluated = true
    toast.add({ severity: 'info', summary: i18n.t('USER_EVALUATED'), detail: '', life: 3000 })
}

const handleReceiverEvaluated = (operationId) => {
    operations.value.filter(o => o.id === operationId)[0].receiverEvaluated = true
    toast.add({ severity: 'info', summary: i18n.t('USER_EVALUATED'), detail: '', life: 3000 })
}

const handleIncidenceRegistered = (updatedOperation) => {
    toast.add({ severity: 'info', summary: i18n.t('INCIDENCE_REGISTERED'), detail: '', life: 3000 })
    const operation = operations.value.filter(o => o.id === updatedOperation.id)
    if (operation.length > 0) {
        operation[0].senderIncidence = updatedOperation.senderIncidence
        operation[0].receiverIncidence = updatedOperation.receiverIncidence
    }
}

// aux functions
function makeOperationFilter() {
    // Fragile solution due to the fact that the UI represent two real states by only entry in the dropdown
    const selectedStatusesIds = selectedStatuses.value.map(s => s.id)
    if (selectedStatusesIds.includes(operationStatusEnum.DEAL_CONFIRMED_BY_SENDER)) {
        selectedStatusesIds.push(operationStatusEnum.DEAL_CONFIRMED_BY_RECEIVER)
    } else if (selectedStatusesIds.includes(operationStatusEnum.DEAL_CONFIRMED_BY_RECEIVER)) {
        selectedStatusesIds.push(operationStatusEnum.DEAL_CONFIRMED_BY_SENDER)
    }
    if (selectedStatusesIds.includes(operationStatusEnum.RETRIEVED_BY_SENDER)) {
        selectedStatusesIds.push(operationStatusEnum.RETRIEVED_BY_RECEIVER)
    } else if (selectedStatusesIds.includes(operationStatusEnum.RETRIEVED_BY_RECEIVER)) {
        selectedStatusesIds.push(operationStatusEnum.RETRIEVED_BY_SENDER)
    }
    return new OperationFilter(selectedOwners.value.map(o => o.id), selectedStatusesIds, selectedOperationTypes.value.map(t => t.id), selectedCreationDate.value, selectedExpirationDate.value)
}

const resetFilter = () => {
    selectedOwners.value = []
    selectedStatuses.value = []
    selectedOperationTypes.value = []
    selectedCreationDate.value = null
    selectedExpirationDate.value = null
}

const makeStatusDropdown = () => {
    for (const key in operationStatusEnum) {
        statuses.value.push(
            {
                id: operationStatusEnum[key],
                name: i18n.t(key),
            }
        )
    }
    statuses.value = uniqBy(statuses.value, 'name')
}
const makeTypesDropdown = () => {
    for (const key in operationTypeEnum) {
        types.value.push(
            {
                id: operationTypeEnum[key],
                name: i18n.t(key),
            }
        )
    }
}

</script>

<style scoped>
.filter_container>* {
    margin: 0.1em;
    width: 15em;
    height: 2.5em;
}

img.filter_avatar {
    margin-left: 0.8em;
    width: 2em;
    height: 2em;
    margin-right: 0.5rem;
    border-radius: 50%;
}
</style>